// Voice preference order (best → fallback). Evaluated against whatever voices
// the platform currently exposes; an empty list is fine (default voice is used).
// The goal is to pick the highest-quality English voice that's actually present
// on Windows (Edge "… Online (Natural)"), Android/Chrome (Google), and
// iOS/macOS ("(Enhanced)"/"(Premium)" / Siri).
const VOICE_PREFERENCES: ((v: SpeechSynthesisVoice) => boolean)[] = [
    // Top tier: explicit high-quality markers in the voice name.
    //   Windows/Edge: "Microsoft Aria Online (Natural) …"; iOS/macOS: "(Enhanced)"/"(Premium)".
    (v) => v.lang.startsWith('en') && /natural|neural|premium|enhanced/i.test(v.name),
    // Edge network voices that label themselves only "Online" (still neural).
    (v) => v.lang.startsWith('en') && /\bonline\b/i.test(v.name),
    // iOS/macOS Siri + Apple's better named voices when Enhanced isn't installed.
    (v) => v.lang.startsWith('en') && /\b(siri|samantha|ava|allison|serena|zoe|evan|nathan|tom)\b/i.test(v.name),
    // Android/Chrome: Google voices are the good ones.
    (v) => v.lang.startsWith('en') && v.name.includes('Google'),
    // Windows local Microsoft voices (Zira/David/Mark/Aria/Guy …).
    (v) => v.lang.startsWith('en') && v.name.includes('Microsoft'),
    // Any English voice.
    (v) => v.lang.startsWith('en'),
    // Last resort: the platform default voice.
    (v) => v.default,
];

function pickVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
    for (const pref of VOICE_PREFERENCES) {
        const v = voices.find(pref);
        if (v) return v;
    }
    return null;
}

// Cached voice list. Edge fetches its high-quality "… Online (Natural)" voices
// asynchronously *after* the first getVoices() call, and iOS reports voices
// unreliably — so we kick off loading at startup (bottom of file) and keep the
// cache fresh via 'voiceschanged'. speakMessage reads this synchronously and
// never awaits (awaiting can hang on iOS).
let _cachedVoices: SpeechSynthesisVoice[] = [];

function refreshVoices(): SpeechSynthesisVoice[] {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return _cachedVoices;
    const v = window.speechSynthesis.getVoices();
    if (v.length) _cachedVoices = v; // keep last good list if a later call returns empty
    return _cachedVoices;
}

let _unlocked = false;

/**
 * Unlocks speech synthesis on iOS Safari, which silently ignores speak() until
 * it has first been invoked from within a user gesture. Speaks an inaudible
 * (volume 0) but NON-EMPTY utterance to flip the engine into the "user-activated"
 * state.
 *
 * CRITICAL: this must be called from a *completed-tap* gesture — `touchend`/`click`.
 * iOS does NOT treat `pointerdown`/`touchstart` as a speech-activating gesture, so an
 * unlock fired from those is silently dropped (no events, no sound) and every later
 * non-gesture speak() stays blocked. See the listener wiring at the bottom of this file.
 * Idempotent — only the first call matters.
 */
export function unlockSpeech(): void {
    if (_unlocked || typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    _unlocked = true;
    try {
        const synth = window.speechSynthesis;
        // On iOS the engine can sit in a stuck/paused state after load, so the first
        // speak() goes 'pending' and never starts. cancel() clears that stuck queue and
        // resume() un-pauses; both are no-ops in the normal case.
        synth.cancel();
        synth.resume();
        const u = new SpeechSynthesisUtterance('.');
        u.volume = 0;
        synth.speak(u);
    } catch {
        // ignore
    }
}

/**
 * Speaks a message via the Web Speech API, falling back to Fully Kiosk TTS.
 *
 * iOS notes (it works on Android/desktop regardless):
 *  - Never awaits before speak(). iOS only allows speech started synchronously,
 *    and getVoices()/'voiceschanged' is unreliable there — awaiting could hang
 *    and the utterance would never be spoken.
 *  - iOS ignores speak() until unlocked by a user gesture; unlockSpeech() is
 *    wired to the first user interaction (bottom of this file).
 */
export const speakMessage = (message: string): void => {
    if (typeof window === 'undefined') return;

    if ('speechSynthesis' in window) {
        const synth = window.speechSynthesis;
        // iOS suspends the engine when idle / when the screen dims (common in kiosk
        // + guard use). resume() revives it and is a no-op otherwise. Must run before
        // speak() or the utterance is silently dropped.
        synth.resume();
        // Only cancel when something is actually queued/playing — an idle cancel()
        // can swallow the next utterance on iOS.
        if (synth.speaking || synth.pending) synth.cancel();

        const utterance = new SpeechSynthesisUtterance(message);
        utterance.rate = 0.9;
        utterance.pitch = 1.0;
        utterance.volume = 0.8;

        // Best-effort voice from the warmed/cached list; may be empty (esp. on
        // iOS at startup), in which case the platform default is used. No await.
        const voice = pickVoice(refreshVoices());
        if (voice) utterance.voice = voice;

        synth.speak(utterance);
    } else if ('fully' in window) {
        const thisWindow = window as any;
        try {
            thisWindow.fully.textToSpeech(message, 'en-us-x-tpd-local');
        } catch {
            // Ignore errors
        }
    }
};

// Warm up voice loading at startup so Edge's async "… Online (Natural)" voices
// are present by the time the first message is spoken (otherwise only the local
// robotic SAPI voices are available), and keep the cache fresh as they arrive.
if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    refreshVoices();
    try {
        window.speechSynthesis.addEventListener('voiceschanged', refreshVoices);
    } catch {
        // ignore
    }
}

// Unlock iOS speech on the first user interaction anywhere (covers kiosk + guard).
// Use completed-tap gestures only — `touchend`/`click` (plus `keydown` for desktop).
// NOT `pointerdown`/`touchstart`: iOS does not treat those as speech-activating gestures,
// so unlocking from them silently fails and all later non-gesture speech stays blocked.
if (typeof document !== 'undefined') {
    const onFirstInteraction = () => {
        unlockSpeech();
        document.removeEventListener('touchend', onFirstInteraction);
        document.removeEventListener('click', onFirstInteraction);
        document.removeEventListener('keydown', onFirstInteraction);
    };
    document.addEventListener('touchend', onFirstInteraction);
    document.addEventListener('click', onFirstInteraction);
    document.addEventListener('keydown', onFirstInteraction);
}
